import { useState, useRef, useEffect } from 'react'

export default function TextBlock({ settings, onInlineUpdate }) {
  const { padding, margin, backgroundColor, borderWidth, borderColor, borderRadius } = settings
  const [isEditing, setIsEditing] = useState(false)
  const editorRef = useRef(null)

  const containerStyle = {
    padding: `${padding.top} ${padding.right} ${padding.bottom} ${padding.left}`,
    margin: `${margin.top} ${margin.right} ${margin.bottom} ${margin.left}`,
    backgroundColor: backgroundColor || 'transparent',
    border: (borderWidth && borderWidth !== '0px') ? `${borderWidth} solid ${borderColor || '#cccccc'}` : 'none',
    borderRadius: borderRadius || '0px',
    ...(settings.backgroundImage ? {
      backgroundImage: `url(${settings.backgroundImage})`,
      backgroundSize: settings.backgroundSize || 'cover',
      backgroundPosition: settings.backgroundPosition || 'center center',
      backgroundRepeat: settings.backgroundRepeat || 'no-repeat',
      position: 'relative'
    } : {})
  }

  const textStyle = {
    fontSize: settings.fontSize,
    fontFamily: settings.fontFamily,
    fontWeight: settings.fontWeight,
    color: settings.color,
    textAlign: settings.textAlign,
    lineHeight: settings.lineHeight,
    outline: 'none',
    cursor: isEditing ? 'text' : 'inherit',
    minHeight: isEditing ? '1.5em' : 'auto',
    borderBottom: isEditing ? '2px solid var(--color-primary)' : 'none',
    transition: 'border-bottom 0.15s'
  }

  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.focus()
      // Place cursor at end
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(editorRef.current)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }, [isEditing])

  const handleDoubleClick = (e) => {
    if (onInlineUpdate) {
      e.stopPropagation()
      setIsEditing(true)
    }
  }

  const handleBlur = () => {
    if (isEditing && editorRef.current && onInlineUpdate) {
      onInlineUpdate({ content: editorRef.current.innerHTML })
      setIsEditing(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditing(false)
      editorRef.current?.blur()
    }
  }

  if (isEditing) {
    return (
      <div style={containerStyle}>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          style={textStyle}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          dangerouslySetInnerHTML={{ __html: settings.content }}
        />
      </div>
    )
  }

  return (
    <div style={containerStyle} onDoubleClick={handleDoubleClick}>
      <div
        style={textStyle}
        dangerouslySetInnerHTML={{ __html: settings.content }}
      />
      {onInlineUpdate && (
        <div style={{
          position: 'absolute',
          bottom: '4px',
          right: '8px',
          fontSize: '0.6rem',
          color: '#94a3b8',
          opacity: 0,
          transition: 'opacity 0.15s',
          pointerEvents: 'none',
          background: 'rgba(255,255,255,0.8)',
          padding: '1px 6px',
          borderRadius: '3px'
        }}
          className="inline-edit-hint"
        >
          Double-click to edit
        </div>
      )}
    </div>
  )
}
